import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { type PoliceReportAttestationStatus } from '@/services/api/generated/policeReportsService';
import {
  usePoliceReportsListQuery,
  usePoliceReportsTriggerOrchestrationMutation,
} from '@/services/api/policeReportsService';

import { usePoliceOverviewNavigation } from './hooks/usePoliceOverviewNavigation';
import { PoliceDetails } from './police-details/PoliceDetails';
import { PoliceOverviewErrorState } from './PoliceOverviewErrorState';
import { PoliceOverviewHeader } from './PoliceOverviewHeader';
import { PoliceOverviewLoadingState } from './PoliceOverviewLoadingState';
import { PoliceOverviewMainContent } from './PoliceOverviewMainContent';

interface PoliceOverviewPageProps {
  matterId: string;
}

export const PoliceOverviewPage = ({ matterId }: PoliceOverviewPageProps) => {
  const { police_report_id } = useParams<{ police_report_id: string }>();
  const [searchParams] = useSearchParams();
  const [selectedReportId, setSelectedReportId] = useState<string | null>(
    police_report_id || null,
  );
  const [selectionMode, setSelectionMode] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Navigation handlers
  const {
    handleReportSelect,
    handleBackToList,
    handleBackToCoverage,
    handleCreateReport,
  } = usePoliceOverviewNavigation({
    matterId,
    setSelectedReportId,
  });

  // Update selected report when URL parameter changes
  useEffect(() => {
    setSelectedReportId(police_report_id || null);
  }, [police_report_id]);

  const {
    data: policeReports,
    isLoading,
    error,
    refetch,
    isFetching,
  } = usePoliceReportsListQuery(
    {
      matterId: matterId?.trim() || '',
    },
    {
      skip: !matterId?.trim(),
    },
  );

  const [triggerOrchestration] = usePoliceReportsTriggerOrchestrationMutation();

  // Handle errors with user feedback
  useEffect(() => {
    if (error && 'error' in error) {
      const errorMessage =
        typeof error.error === 'string'
          ? error.error
          : 'Failed to load police reports';

      if (errorMessage !== lastError) {
        toast.error(errorMessage);
        setLastError(errorMessage);
      }
    } else {
      setLastError(null);
    }
  }, [error, lastError]);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(!selectionMode);
  }, [selectionMode]);

  const handleRefresh = useCallback(async () => {
    try {
      // Trigger orchestration first to reconcile state
      await triggerOrchestration({ matterId: matterId.trim() });
      // Then refresh the data
      await refetch();
      toast.success('Police reports refreshed and orchestration triggered');
    } catch (err) {
      // Log error for debugging
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Refresh error:', err);
      }
      toast.error('Failed to refresh police reports');
    }
  }, [refetch, triggerOrchestration, matterId]);

  // Validate selected report exists
  useEffect(() => {
    if (
      selectedReportId &&
      policeReports &&
      !policeReports.find((r) => r.id === selectedReportId)
    ) {
      toast.error('Selected police report not found');
      handleBackToList();
    }
  }, [selectedReportId, policeReports, handleBackToList]);

  // Validate matterId after hooks are called
  if (!matterId?.trim()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900">Invalid Matter</h2>
          <p className="mt-2 text-gray-600">No valid matter ID provided.</p>
        </div>
      </div>
    );
  }

  // Calculate status counts for triage display with better error handling
  const statusCounts =
    policeReports?.reduce(
      (acc, report) => {
        const status = report.attestation_status || 'unclear';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<PoliceReportAttestationStatus, number>,
    ) || ({} as Record<PoliceReportAttestationStatus, number>);

  const unclearCount = statusCounts.unclear || 0;
  const selectedReport = policeReports?.find((r) => r.id === selectedReportId);

  // Get the back parameter to determine where to navigate back to
  const backParam = searchParams.get('back');

  // Show detail view if report is selected
  if (selectedReportId && selectedReport) {
    // Determine which back handler to use based on the back parameter
    const onBack =
      backParam === 'case-setup' ? handleBackToCoverage : handleBackToList;
    const backButtonText =
      backParam === 'case-setup' ? 'Back to Coverage' : 'Back to List';

    return (
      <div className="min-h-screen bg-gray-50">
        <PoliceDetails
          matterId={matterId}
          report={selectedReport}
          onBack={onBack}
          onUpdate={handleRefresh}
          backButtonText={backButtonText}
        />
      </div>
    );
  }

  // Show loading state for initial load
  if (isLoading && !policeReports) {
    return <PoliceOverviewLoadingState />;
  }

  // Show error state with retry option
  if (error && !policeReports) {
    const errorMessage =
      'error' in error && typeof error.error === 'string'
        ? error.error
        : 'Failed to load police reports';

    return (
      <PoliceOverviewErrorState
        errorMessage={errorMessage}
        onRetry={handleRefresh}
        isRetrying={isFetching}
      />
    );
  }

  // Show list view with enhanced observability
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PoliceOverviewHeader
        unclearCount={unclearCount}
        statusCounts={statusCounts}
        hasData={!!(policeReports && policeReports.length > 0)}
        selectionMode={selectionMode}
        onToggleSelectionMode={toggleSelectionMode}
        onRefresh={handleRefresh}
        onCreateReport={handleCreateReport}
      />

      <PoliceOverviewMainContent
        matterId={matterId}
        onReportSelect={handleReportSelect}
        onCreateReport={handleCreateReport}
      />
    </div>
  );
};
